export interface SoftwareAppStructuredData {
  name: string
  description: string
  url: string
  applicationCategory: 'BusinessApplication'
  operatingSystem: string[]
  offers: {
    price: number
    priceCurrency: string
  }
  aggregateRating?: {
    ratingValue: number
    reviewCount: number
  }
  review?: {
    reviewRating: {
      ratingValue: number
    }
    author: {
      name: string
    }
    reviewBody: string
  }
  screenshot?: string[]
  softwareVersion?: string
  datePublished?: string
  dateModified?: string
  author?: {
    name: string
    url?: string
  }
  publisher?: {
    name: string
    url?: string
  }
  downloadUrl?: string
  installUrl?: string
  sameAs?: string[]
}

export function createSoftwareAppStructuredData(data: SoftwareAppStructuredData) {
  const structuredData: any = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": data.name,
    "description": data.description,
    "url": data.url,
    "applicationCategory": data.applicationCategory,
    "operatingSystem": data.operatingSystem,
    "offers": {
      "@type": "Offer",
      "price": data.offers.price,
      "priceCurrency": data.offers.priceCurrency
    }
  }

  // Add rating or review (required by Google)
  if (data.aggregateRating) {
    structuredData["aggregateRating"] = {
      "@type": "AggregateRating",
      "ratingValue": data.aggregateRating.ratingValue,
      "reviewCount": data.aggregateRating.reviewCount
    }
  } else if (data.review) {
    structuredData["review"] = {
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": data.review.reviewRating.ratingValue
      },
      "author": {
        "@type": "Person",
        "name": data.review.author.name
      },
      "reviewBody": data.review.reviewBody
    }
  }

  // Add optional properties
  if (data.screenshot) {
    structuredData["screenshot"] = data.screenshot
  }

  if (data.softwareVersion) {
    structuredData["softwareVersion"] = data.softwareVersion
  }

  if (data.datePublished) {
    structuredData["datePublished"] = data.datePublished
  }

  if (data.dateModified) {
    structuredData["dateModified"] = data.dateModified
  }

  if (data.author) {
    structuredData["author"] = {
      "@type": "Person",
      "name": data.author.name,
      ...(data.author.url && { "url": data.author.url })
    }
  }

  if (data.publisher) {
    structuredData["publisher"] = {
      "@type": "Organization",
      "name": data.publisher.name,
      ...(data.publisher.url && { "url": data.publisher.url })
    }
  }

  if (data.downloadUrl) {
    structuredData["downloadUrl"] = data.downloadUrl
  }

  if (data.installUrl) {
    structuredData["installUrl"] = data.installUrl
  }

  if (data.sameAs) {
    structuredData["sameAs"] = data.sameAs
  }

  return structuredData
}

// Pre-configured data for SourcedIn
export const SOURCEDIN_SOFTWARE_DATA: SoftwareAppStructuredData = {
  name: "SourcedIn - RFP Marketplace Platform",
  description: "The leading B2B marketplace platform connecting buyers and sellers through Request for Proposals. Streamline procurement, find qualified vendors, and grow your business through competitive bidding.",
  url: "https://sourcedin.ai",
  applicationCategory: "BusinessApplication",
  operatingSystem: ["Web Browser", "iOS", "Android"],
  offers: {
    price: 0,
    priceCurrency: "USD"
  },
  aggregateRating: {
    ratingValue: 4.8,
    reviewCount: 127
  },
  screenshot: [
    "https://sourcedin.ai/images/screenshot-dashboard.png",
    "https://sourcedin.ai/images/screenshot-marketplace.png",
    "https://sourcedin.ai/images/screenshot-vendor-profile.png"
  ],
  softwareVersion: "2.1.0",
  datePublished: "2023-01-15",
  dateModified: new Date().toISOString().split('T')[0],
  author: {
    name: "SourcedIn Team",
    url: "https://sourcedin.ai/about"
  },
  publisher: {
    name: "SourcedIn",
    url: "https://sourcedin.ai"
  },
  installUrl: "https://sourcedin.ai/register",
  sameAs: [
    "https://linkedin.com/company/sourcedin",
    "https://twitter.com/sourcedin",
    "https://github.com/sourcedin"
  ]
}
